import { DataLayerContext } from 'features/dataLayers/DataLayerProvider'
import { GeckoBinnedDataBuffer } from 'features/dataLayers/geckoBinnedDataBuffer'
import { DeleteCacheValue } from 'features/rolodex'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { GeckoComponentRect } from 'types'
import { cdfDataFileName } from 'utils/constants'
import type { MemoizedFunction } from 'utils/lodashTypes'

type CdfId = string
type FilePath = string
type GenerationNumber = number
type CropSelection = string

interface CdfBinnedDataStorageFns {
  getCachedBinnedDataBufferAccessor: (
    id: CdfId,
    filePath?: FilePath,
    cropSelection?: GeckoComponentRect
  ) =>
    | {
        retrieveBinnedDataBuffer: () => Promise<GeckoBinnedDataBuffer>
        generation: GenerationNumber | undefined
      }
    | undefined

  putBinnedDataBufferInCache: (props: {
    id: CdfId
    filePath?: FilePath
    generation: GenerationNumber
    data: (() => Promise<GeckoBinnedDataBuffer>) & MemoizedFunction
    cropSelection?: GeckoComponentRect
  }) => void
  deleteCachedBinnedDataBuffer: (
    id: CdfId,
    filePath?: FilePath,
    cropSelection?: GeckoComponentRect
  ) => void
}

function gcrToID(gcr: GeckoComponentRect | undefined) {
  return gcr ? JSON.stringify(gcr.toJSON()) : 'NO_SELECTION'
}

/**
 * A hook that returns utility functions for storing and retrieving binned data
 */
export function useBinnedDataBufferCache(): CdfBinnedDataStorageFns {
  const { cdfDataFetchCache, setInCdfDataFetchCache } =
    useContext(DataLayerContext)

  // ----------------------------------------------------------------------------
  // Handle Temporary Data

  // a ledger of the data uploaded via this hook, so we can clear it on unmount if needed
  const uploadedDataLedger = useRef<
    Map<CdfId, Map<CropSelection, Map<FilePath, GenerationNumber>>>
  >(new Map())

  const upsertInLedger = useCallback(
    (props: {
      id: CdfId
      filePath: FilePath
      cropSelection: CropSelection
      generation: GenerationNumber
    }) => {
      const ledgerIdValue = getMapWithDefault(
        uploadedDataLedger.current,
        props.id,
        new Map()
      )
      const ledgerCopSelectionValue = getMapWithDefault(
        ledgerIdValue,
        props.cropSelection,
        new Map()
      )
      ledgerCopSelectionValue.set(props.filePath, props.generation)
    },
    []
  )

  const deleteInLedger = useCallback(
    (props: {
      id: CdfId
      cropSelection: CropSelection
      filePath: FilePath
    }) => {
      uploadedDataLedger.current
        .get(props.id)
        ?.get(props.cropSelection)
        ?.delete(props.filePath)
    },
    []
  )

  // a function to remove any data in the cdfDataFetchCache that was uploaded via this hook
  const clearTemporaryData = useCallback(() => {
    for (const [id, fileMap] of uploadedDataLedger.current) {
      for (const [cropSelection, selectionMap] of fileMap) {
        for (const [filePath, generation] of selectionMap) {
          const generationNumInCache = cdfDataFetchCache
            .get(id)
            ?.get(cropSelection)
            ?.get(filePath)?.generation
          // clear all temporary data out of the cache
          // we can tell if the data was from this hook by checking the generation number
          if (generationNumInCache === generation) {
            setInCdfDataFetchCache({
              id,
              filePath: filePath,
              cropSelection,
              value: DeleteCacheValue,
            })
          }
        }
      }
    }

    uploadedDataLedger.current.clear()
  }, [
    // should never re-initialize (ref variable)
    cdfDataFetchCache,
    // should never change (only depends on ref variable)
    setInCdfDataFetchCache,
  ])

  useEffect(() => {
    return clearTemporaryData
  }, [clearTemporaryData])

  // ----------------------------------------------------------------------------
  // Main Functions

  const getCachedBinnedDataBufferAccessor: CdfBinnedDataStorageFns['getCachedBinnedDataBufferAccessor'] =
    useCallback(
      (
        id: CdfId,
        filePath: FilePath = cdfDataFileName,
        cropSelection = undefined
      ) => {
        const specificIdCache = cdfDataFetchCache.get(id)
        const specificCropCache = specificIdCache?.get(gcrToID(cropSelection))
        if (specificCropCache) {
          const cachedValues = specificCropCache?.get(filePath)
          if (cachedValues) {
            return {
              retrieveBinnedDataBuffer: cachedValues.getData,
              generation: cachedValues.generation,
            }
          }
        }
        return undefined
      },
      [cdfDataFetchCache]
    )

  const putBinnedDataBufferInCache: CdfBinnedDataStorageFns['putBinnedDataBufferInCache'] =
    useCallback(
      (props: {
        id: CdfId
        filePath?: FilePath
        generation: GenerationNumber
        cropSelection?: GeckoComponentRect
        data: (() => Promise<GeckoBinnedDataBuffer>) & MemoizedFunction
      }) => {
        const {
          id,
          filePath = cdfDataFileName,
          generation,
          cropSelection,
          data,
        } = props

        const cropSelectionId = gcrToID(cropSelection)

        setInCdfDataFetchCache({
          id,
          filePath,
          value: {
            generation,
            getData: data,
          },
          cropSelection: cropSelectionId,
        })
        upsertInLedger({
          id,
          filePath,
          cropSelection: cropSelectionId,
          generation,
        })
      },
      [setInCdfDataFetchCache, upsertInLedger]
    )

  const deleteCachedBinnedDataBuffer: CdfBinnedDataStorageFns['deleteCachedBinnedDataBuffer'] =
    useCallback(
      (
        id: CdfId,
        filePath: FilePath = cdfDataFileName,
        cropSelection = undefined
      ) => {
        const cropSelectionId = gcrToID(cropSelection)
        setInCdfDataFetchCache({
          id,
          filePath: filePath,
          cropSelection: cropSelectionId,
          value: DeleteCacheValue,
        })
        deleteInLedger({
          id,
          filePath: filePath,
          cropSelection: cropSelectionId,
        })
      },
      [setInCdfDataFetchCache, deleteInLedger]
    )

  return {
    getCachedBinnedDataBufferAccessor,
    putBinnedDataBufferInCache,
    deleteCachedBinnedDataBuffer,
  }
}

/**
 * A function to get a map value.  If there is no map value for the given key, this function
 * adds the default value to the map and returns it.
 *
 * NOTE: **THIS FUNCTION MUTATES THE MAP IN-PLACE**
 *
 * @param map
 * @param key
 * @param defaultValue
 */
function getMapWithDefault<MAP_KEY, MAP_VALUE>(
  map: Map<MAP_KEY, MAP_VALUE>,
  key: NoInfer<MAP_KEY>,
  defaultValue: NoInfer<MAP_VALUE>
): MAP_VALUE {
  const existingValue = map.get(key)
  if (existingValue) return existingValue
  map.set(key, defaultValue)
  return defaultValue
}
